import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * RiverOfLifeSection — polish pass.
 *
 * Cinematic "river of life" beat on the marketing landing page.
 * Sits between the "scattered" hero beat and the Camera Roll break.
 *
 * Design goals:
 *   1. Zero blank/empty cards — every slot has real emotional imagery
 *      or an intentional, premium crafted visual.
 *   2. Image ↔ label mapping that reads instantly.
 *   3. Luminous golden ribbon with motion (drifting sparks that follow
 *      the path via <animateMotion>) — a clear sense of moments flowing
 *      into one story.
 *   4. Central "Our Life Story" book is the unmistakable focal point:
 *      larger, breathing glow halo, embossed infinity monogram.
 *   5. Organic vertical stagger breaks the 3-row grid feel; each card
 *      enters with a slight rotation offset for life.
 *   6. Mobile collapses cleanly to a vertical river.
 */

const C = {
  bg:      '#0A0A0A',
  bgAlt:   '#141414',
  gold:    '#D4A574',
  goldLt:  '#E0B989',
  goldDk:  '#B8895A',
  text:    '#FFFFFF',
  textSec: '#B8B8B8',
  textMut: '#666666',
  border:  '#2A2A2A',
};
const serif = "'Georgia', 'Times New Roman', serif";

/* ─────────────────────────────────────────────────────────────
   Asset mapping (polished):
   - 5 local /memories photos
   - 2 Unsplash photos already proven-live in SectionCameraRoll
   - 1 crafted "open book" inline SVG/CSS for Turn memories into books
     (intentional artifact; stronger than any generic book photo)
   ───────────────────────────────────────────────────────────── */
const MOMENTS = [
  {
    label: 'Capture the moment',
    kind:  'photo',
    src:   '/memories/steps.jpg',          // first steps — emotional "capture"
    alt:   'A child taking first steps toward the camera',
  },
  {
    label: 'Build your life story',
    kind:  'photo',
    src:   '/memories/couple.jpg',         // foundational relationship moment
    alt:   'A couple embracing at golden hour',
  },
  {
    label: 'Create life movies',
    kind:  'photo',
    // Same warm-lifestyle CDN set that SectionCameraRoll uses (pre-validated on prod)
    src:   'https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=480&q=70&auto=format&fit=crop',
    alt:   'Friends gathered at sunset — a scene from a life movie',
  },
  {
    label: 'Turn memories into books',
    kind:  'book',                         // crafted visual, not a photo
    alt:   'An open photo book with warm pages',
  },
  {
    label: 'Share albums',
    kind:  'photo',
    src:   '/memories/summer.jpg',         // warm family / summer lifestyle
    alt:   'A warm summer afternoon, the kind of day worth sharing',
  },
  {
    label: 'Send to loved ones',
    kind:  'photo',
    // Parent carrying two young children on his shoulders — warm, family, relational
    src:   'https://images.unsplash.com/photo-1609220136736-443140cffec6?w=480&q=70&auto=format&fit=crop',
    alt:   'A parent carrying two children on his shoulders — a memory meant for loved ones',
  },
  {
    label: 'Post to social',
    kind:  'photo',
    // Long-table dinner with friends — the classic shareable celebration moment
    src:   'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=480&q=70&auto=format&fit=crop',
    alt:   'Friends gathered around a long table — a celebration worth sharing',
  },
  {
    label: 'Start a story for someone you love',
    kind:  'photo',
    // Child on a bench with an open book — literally "once upon a time"
    src:   'https://images.unsplash.com/photo-1472162072942-cd5147eb3902?w=480&q=70&auto=format&fit=crop',
    alt:   'A child reading on a sunlit bench — the beginning of someone’s story',
  },
];

// Subtle per-card vertical offset (px) → organic wave that visually
// rides the ribbon instead of feeling like a rigid 3×3 grid.
const YSTAGGER = [-14, 10, -6, 8, -4, -12, 6, 14];

/* ─────────────────────────────────────────────────────────────
   Crafted "open photo book" visual — used where a real photo
   would feel weaker than an intentional design artifact.
   ───────────────────────────────────────────────────────────── */
function OpenBookVisual() {
  return (
    <div className="relative w-full h-full overflow-hidden" aria-hidden>
      {/* Deep warm backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(ellipse at 50% 60%, rgba(184,137,90,0.35) 0%, rgba(26,19,13,0.95) 70%)`,
        }}
      />
      {/* The open book SVG */}
      <svg
        viewBox="0 0 172 208"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="pageLeft" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%"   stopColor="#1b140e" />
            <stop offset="92%"  stopColor="#2a1f15" />
            <stop offset="100%" stopColor="#15100a" />
          </linearGradient>
          <linearGradient id="pageRight" x1="1" x2="0" y1="0" y2="0">
            <stop offset="0%"   stopColor="#1b140e" />
            <stop offset="92%"  stopColor="#2a1f15" />
            <stop offset="100%" stopColor="#15100a" />
          </linearGradient>
          <radialGradient id="spineGlow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%"   stopColor={C.goldLt} stopOpacity="0.55" />
            <stop offset="55%"  stopColor={C.gold}   stopOpacity="0.18" />
            <stop offset="100%" stopColor={C.gold}   stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Shadow under book */}
        <ellipse cx="86" cy="186" rx="62" ry="6" fill="#000" opacity="0.55" />

        {/* Left page */}
        <path
          d="M 20 50 Q 22 46 28 46 L 84 46 L 86 160 L 28 162 Q 22 162 20 158 Z"
          fill="url(#pageLeft)"
          stroke={`${C.gold}55`}
          strokeWidth="0.6"
        />
        {/* Right page */}
        <path
          d="M 152 50 Q 150 46 144 46 L 88 46 L 86 160 L 144 162 Q 150 162 152 158 Z"
          fill="url(#pageRight)"
          stroke={`${C.gold}55`}
          strokeWidth="0.6"
        />

        {/* Spine glow */}
        <rect x="78" y="40" width="16" height="126" fill="url(#spineGlow)" />

        {/* Left-page content hints — photo frame + text lines */}
        <rect x="32" y="58" width="44" height="32" rx="1.5"
              fill={`${C.gold}1a`} stroke={`${C.gold}55`} strokeWidth="0.5" />
        <line x1="32" y1="100" x2="76" y2="100" stroke={`${C.gold}55`} strokeWidth="0.5" />
        <line x1="32" y1="108" x2="72" y2="108" stroke={`${C.gold}33`} strokeWidth="0.5" />
        <line x1="32" y1="116" x2="74" y2="116" stroke={`${C.gold}33`} strokeWidth="0.5" />
        <line x1="32" y1="124" x2="68" y2="124" stroke={`${C.gold}33`} strokeWidth="0.5" />

        {/* Right-page content hints — text lines + photo frame */}
        <line x1="96" y1="58"  x2="140" y2="58"  stroke={`${C.gold}55`} strokeWidth="0.5" />
        <line x1="96" y1="66"  x2="136" y2="66"  stroke={`${C.gold}33`} strokeWidth="0.5" />
        <line x1="96" y1="74"  x2="138" y2="74"  stroke={`${C.gold}33`} strokeWidth="0.5" />
        <rect x="96" y="86" width="44" height="32" rx="1.5"
              fill={`${C.gold}1a`} stroke={`${C.gold}55`} strokeWidth="0.5" />
        <line x1="96"  y1="126" x2="140" y2="126" stroke={`${C.gold}33`} strokeWidth="0.5" />
        <line x1="96"  y1="134" x2="134" y2="134" stroke={`${C.gold}33`} strokeWidth="0.5" />

        {/* Page edge highlights — top */}
        <line x1="20" y1="46" x2="84" y2="46" stroke={`${C.gold}66`} strokeWidth="0.5" />
        <line x1="88" y1="46" x2="152" y2="46" stroke={`${C.gold}66`} strokeWidth="0.5" />

        {/* Tiny infinity monogram at bottom-center of spread */}
        <g transform="translate(70 170) scale(0.18)">
          <path
            d="M50,50 C50,22 90,22 100,50 C110,78 150,78 150,50 C150,22 110,22 100,50 C90,78 50,78 50,50 Z"
            fill="none" stroke={C.gold} strokeWidth="9"
            strokeLinecap="round" strokeLinejoin="round" opacity="0.85"
          />
        </g>
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Memory card (photo OR crafted book) + glass gold-bordered label
   ───────────────────────────────────────────────────────────── */
function MomentCard({ moment, index, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  // Slight alternating rotation on entrance → organic "settled into place"
  const restRot = index % 2 === 0 ? -0.6 : 0.6;

  return (
    <motion.div
      ref={ref}
      data-testid={`river-moment-card-${index}`}
      initial={{ opacity: 0, y: 34, rotate: restRot * 4 }}
      animate={inView ? { opacity: 1, y: 0, rotate: restRot } : {}}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex flex-col items-center gap-3"
    >
      <motion.div
        animate={{ y: [0, -4, 0, -2, 0] }}
        transition={{ duration: 8 + (index % 3), repeat: Infinity, ease: 'easeInOut', delay: (index * 0.4) }}
        className="relative"
        style={{
          width: 172,
          height: 208,
          borderRadius: 10,
          overflow: 'hidden',
          border: `1px solid ${C.gold}3d`,
          boxShadow:
            `0 1px 0 rgba(255,255,255,0.05) inset,` +
            `0 28px 56px -20px rgba(0,0,0,0.72),` +
            `0 0 0 1px rgba(212,165,116,0.10),` +
            `0 0 42px -14px rgba(212,165,116,0.35)`,  // warm outer halo
          background: '#141414',
        }}
      >
        {moment.kind === 'book' ? (
          <OpenBookVisual />
        ) : (
          <img
            src={moment.src}
            alt={moment.alt || ''}
            loading="lazy"
            className="w-full h-full object-cover"
            style={{ filter: 'saturate(0.95) contrast(1.04)' }}
          />
        )}
        {/* Warm vignette for tonal unity */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 55%, transparent 50%, rgba(10,8,6,0.55) 100%)',
          }}
        />
        {/* Hairline warm sheen across top edge */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold}66 50%, transparent 100%)` }}
        />
      </motion.div>

      {/* Glassy gold-bordered label */}
      <div
        className="text-[11px] sm:text-xs tracking-[0.06em] px-3 py-1.5 whitespace-nowrap"
        style={{
          color: C.text,
          background: 'rgba(10,10,10,0.65)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: `1px solid ${C.gold}4d`,
          borderRadius: 999,
          fontFamily: "'DM Sans', system-ui, sans-serif",
          fontWeight: 400,
          boxShadow: `0 6px 20px -8px rgba(0,0,0,0.7)`,
        }}
      >
        {moment.label}
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Central "Our Life Story" book — the focal point.
   Upgrades vs previous pass:
     • Larger outer radial halo + slow-breathing opacity
     • Bigger footprint (264×328)
     • Embossed infinity monogram above the title
     • Subtle continuous scale breath 0.995 ↔ 1.005
   ───────────────────────────────────────────────────────────── */
function CenterBook() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <div className="relative flex items-center justify-center" style={{ width: 320, height: 380 }}>
      {/* Breathing outer halo */}
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 420, height: 420,
          background:
            'radial-gradient(circle, rgba(224,185,137,0.22) 0%, rgba(212,165,116,0.08) 35%, transparent 70%)',
          filter: 'blur(6px)',
        }}
        animate={{ opacity: [0.55, 0.95, 0.55], scale: [0.96, 1.04, 0.96] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        ref={ref}
        data-testid="river-center-book"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 1.3, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
        style={{ width: 264, height: 328 }}
      >
        {/* Book-stack depth: back + middle pages */}
        <div
          aria-hidden
          className="absolute"
          style={{
            left: 12, top: 14, right: -12, bottom: -10,
            borderRadius: 6,
            background: '#141414',
            border: `1px solid ${C.border}`,
            transform: 'rotate(2.2deg)',
            boxShadow: '0 40px 70px -24px rgba(0,0,0,0.75)',
          }}
        />
        <div
          aria-hidden
          className="absolute"
          style={{
            left: 6, top: 8, right: -6, bottom: -5,
            borderRadius: 6,
            background: '#1a1814',
            border: `1px solid ${C.border}`,
            transform: 'rotate(-1.1deg)',
          }}
        />

        {/* Slow-breathing front cover */}
        <motion.div
          animate={{ scale: [0.995, 1.005, 0.995] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
          style={{
            borderRadius: 9,
            background:
              `linear-gradient(160deg, rgba(28,21,15,0.99) 0%, rgba(38,29,20,0.99) 55%, rgba(20,15,10,0.99) 100%)`,
            border: `1px solid ${C.gold}73`,
            boxShadow:
              `0 0 0 1px rgba(212,165,116,0.14),` +
              `0 50px 100px -28px rgba(0,0,0,0.9),` +
              `inset 0 1px 0 rgba(255,255,255,0.06),` +
              `0 0 60px -12px rgba(212,165,116,0.35)`,
          }}
        >
          {/* Gold filigree corner marks */}
          <svg
            aria-hidden
            width="264" height="328"
            viewBox="0 0 264 328"
            className="absolute inset-0 pointer-events-none"
            style={{ opacity: 0.6 }}
          >
            <g stroke={C.gold} strokeWidth="0.9" fill="none" strokeLinecap="round">
              <path d="M18,32 L18,18 L32,18" />
              <path d="M246,32 L246,18 L232,18" />
              <path d="M18,296 L18,310 L32,310" />
              <path d="M246,296 L246,310 L232,310" />
            </g>
          </svg>

          {/* Official EverBook brand mark */}
          <img
            aria-hidden
            src="/brand/everbook-mark-official.png"
            alt=""
            width="42"
            height={42 / (576 / 359)}
            style={{
              marginBottom: 18,
              objectFit: 'contain',
              filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.7))',
            }}
          />

          <div
            className="text-[10px] tracking-[0.32em] mb-3"
            style={{
              color: C.gold,
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontWeight: 500,
            }}
          >
            EVERBOOK
          </div>

          <h3
            className="text-2xl sm:text-3xl leading-tight"
            style={{ color: C.text, fontFamily: serif, fontWeight: 500, letterSpacing: '-0.01em' }}
          >
            Our Life Story
          </h3>

          <div
            className="mt-5 w-20 h-px"
            aria-hidden
            style={{ background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)` }}
          />

          <div
            className="mt-4 text-[11px] tracking-[0.25em]"
            style={{ color: C.textSec, fontFamily: "'DM Sans', system-ui, sans-serif" }}
          >
            2010 &mdash; TODAY
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Ambient gold dust — desktop only, static positions, slow drift
   ───────────────────────────────────────────────────────────── */
function GoldParticles() {
  const particles = [
    { x: 6,  y: 14, size: 2, dur: 18, delay: 0 },
    { x: 20, y: 60, size: 2, dur: 22, delay: 3 },
    { x: 36, y: 28, size: 3, dur: 24, delay: 1 },
    { x: 52, y: 82, size: 2, dur: 20, delay: 5 },
    { x: 66, y: 16, size: 2, dur: 19, delay: 2 },
    { x: 80, y: 52, size: 3, dur: 23, delay: 4 },
    { x: 90, y: 30, size: 2, dur: 21, delay: 6 },
    { x: 46, y: 48, size: 2, dur: 17, delay: 2 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: C.goldLt,
            boxShadow: `0 0 ${p.size * 5}px ${C.gold}aa`,
          }}
          animate={{ y: [-8, 8, -8], opacity: [0.2, 0.85, 0.2] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   The Section
   ───────────────────────────────────────────────────────────── */
export default function RiverOfLifeSection() {
  const headRef = useRef(null);
  const headInView = useInView(headRef, { once: true, margin: '-80px' });

  const row1 = MOMENTS.slice(0, 3);
  const row2 = MOMENTS.slice(3, 5);  // flanks the center book
  const row3 = MOMENTS.slice(5, 8);

  // Shared ribbon path — referenced by both the base stroke and the
  // <animateMotion> sparks so they literally travel along the ribbon.
  const ribbonD =
    "M 40 130 C 240 50, 400 230, 600 170 S 900 90, 960 150 " +
    "L 960 440 C 760 530, 520 370, 320 470 S 80 570, 40 480 " +
    "L 40 750 C 240 670, 480 830, 700 770 S 940 710, 960 790";

  return (
    <section
      className="relative overflow-hidden"
      data-testid="river-of-life-section"
      style={{
        background: C.bg,
        paddingTop: '5rem',
        paddingBottom: '7rem',
      }}
    >
      {/* Ambient warm wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 55% at 50% 45%, rgba(212,165,116,0.07) 0%, rgba(212,165,116,0.025) 45%, transparent 75%)',
        }}
      />

      {/* Top hairline divider */}
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${C.gold}66, transparent)` }}
      />

      {/* ─── Heading ─── */}
      <div ref={headRef} className="relative max-w-3xl mx-auto px-6 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          animate={headInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
          className="text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight"
          style={{ color: C.text, fontFamily: serif, fontWeight: 400, letterSpacing: '-0.01em' }}
          data-testid="river-title"
        >
          A life, <span className="italic" style={{ color: C.goldLt }}>gently</span> brought together
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={headInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.0, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 text-base sm:text-lg lg:text-xl leading-[1.7] max-w-[620px] mx-auto"
          style={{ color: C.textSec }}
          data-testid="river-subtitle"
        >
          Every photo, video, voice note, and memory has a place. EverBook turns
          scattered moments into a living story you can relive, share, and pass on.
        </motion.p>
      </div>

      {/* ─── DESKTOP layout ─── */}
      <div
        className="hidden md:block relative max-w-6xl mx-auto px-6 mt-20"
        style={{ minHeight: 960 }}
      >
        <GoldParticles />

        {/* The golden ribbon */}
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1000 900"
          preserveAspectRatio="none"
        >
          <defs>
            {/* Gradient along the length of the ribbon */}
            <linearGradient id="riverGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor={C.gold}   stopOpacity="0" />
              <stop offset="10%"  stopColor={C.gold}   stopOpacity="0.6" />
              <stop offset="50%"  stopColor={C.goldLt} stopOpacity="0.95" />
              <stop offset="90%"  stopColor={C.gold}   stopOpacity="0.6" />
              <stop offset="100%" stopColor={C.gold}   stopOpacity="0" />
            </linearGradient>
            {/* Luminous bloom around the ribbon */}
            <filter id="ribbonBloom" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            {/* Path we reuse for sparks' <animateMotion> */}
            <path id="riverPath" d={ribbonD} />
          </defs>

          {/* Soft bloom layer */}
          <use
            href="#riverPath"
            stroke={C.gold}
            strokeOpacity="0.32"
            strokeWidth="7"
            fill="none"
            strokeLinecap="round"
            filter="url(#ribbonBloom)"
          />

          {/* Base ribbon */}
          <use
            href="#riverPath"
            stroke="url(#riverGrad)"
            strokeWidth="2.2"
            fill="none"
            strokeLinecap="round"
          />

          {/* Traveling shimmer — dashed light that walks the path */}
          <use
            href="#riverPath"
            stroke={C.goldLt}
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            strokeDasharray="3 380"
            opacity="0.8"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0" to="-1520"
              dur="9s"
              repeatCount="indefinite"
            />
          </use>

          {/* Three sparks that literally travel the ribbon */}
          {[
            { dur: 14, begin: '0s',  size: 2.2, opacity: 0.85 },
            { dur: 16, begin: '3.5s', size: 1.8, opacity: 0.7 },
            { dur: 18, begin: '7s',   size: 2.6, opacity: 0.9 },
          ].map((s, i) => (
            <circle
              key={i}
              r={s.size}
              fill={C.goldLt}
              opacity={s.opacity}
              style={{ filter: `drop-shadow(0 0 6px ${C.gold})` }}
            >
              <animateMotion
                dur={`${s.dur}s`}
                repeatCount="indefinite"
                begin={s.begin}
                rotate="auto"
              >
                <mpath href="#riverPath" />
              </animateMotion>
            </circle>
          ))}
        </svg>

        {/* ROW 1 — 3 cards with organic y-offset */}
        <div className="relative grid grid-cols-3 gap-6 pt-4">
          {row1.map((m, i) => (
            <div key={i} className="flex justify-center" style={{ transform: `translateY(${YSTAGGER[i]}px)` }}>
              <MomentCard moment={m} index={i} delay={i * 0.1} />
            </div>
          ))}
        </div>

        {/* ROW 2 — flanking cards + center book */}
        <div className="relative grid grid-cols-[1fr_auto_1fr] gap-10 mt-16 items-center">
          <div className="flex justify-center" style={{ transform: `translateY(${YSTAGGER[3]}px)` }}>
            <MomentCard moment={row2[0]} index={3} delay={0.28} />
          </div>
          <div className="flex justify-center">
            <CenterBook />
          </div>
          <div className="flex justify-center" style={{ transform: `translateY(${YSTAGGER[4]}px)` }}>
            <MomentCard moment={row2[1]} index={4} delay={0.36} />
          </div>
        </div>

        {/* ROW 3 — 3 cards with organic y-offset */}
        <div className="relative grid grid-cols-3 gap-6 mt-16">
          {row3.map((m, i) => (
            <div key={i} className="flex justify-center" style={{ transform: `translateY(${YSTAGGER[i + 5]}px)` }}>
              <MomentCard moment={m} index={i + 5} delay={0.45 + i * 0.1} />
            </div>
          ))}
        </div>
      </div>

      {/* ─── MOBILE layout: vertical river ─── */}
      <div className="md:hidden relative max-w-md mx-auto px-6 mt-16">
        <div
          aria-hidden
          className="absolute top-4 bottom-4 left-1/2 -translate-x-1/2 w-px"
          style={{
            background: `linear-gradient(180deg, transparent 0%, ${C.gold}80 8%, ${C.gold}80 92%, transparent 100%)`,
          }}
        />
        <motion.span
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 rounded-full"
          style={{
            width: 6, height: 6,
            background: C.goldLt,
            boxShadow: `0 0 18px ${C.gold}`,
          }}
          animate={{ top: ['5%', '95%'] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative flex flex-col items-center gap-10">
          {MOMENTS.slice(0, 3).map((m, i) => (
            <MomentCard key={`m-${i}`} moment={m} index={i} delay={i * 0.08} />
          ))}

          <CenterBook />

          {MOMENTS.slice(3).map((m, i) => (
            <MomentCard key={`m-${i + 3}`} moment={m} index={i + 3} delay={(i + 3) * 0.08} />
          ))}
        </div>
      </div>

      {/* ─── Closing ─── */}
      <div className="relative max-w-3xl mx-auto px-6 text-center mt-24 sm:mt-28">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center justify-center gap-4"
          aria-hidden
        >
          <span
            className="h-px flex-1 max-w-24"
            style={{ background: `linear-gradient(90deg, transparent, ${C.gold}66)` }}
          />
          <svg width="22" height="12" viewBox="47.2 19.2 105.6 61.6" fill="none">
            <path
              d="M50,50 C50,22 90,22 100,50 C110,78 150,78 150,50 C150,22 110,22 100,50 C90,78 50,78 50,50 Z"
              stroke={C.gold} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
          <span
            className="h-px flex-1 max-w-24"
            style={{ background: `linear-gradient(-90deg, transparent, ${C.gold}66)` }}
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 1.2, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 italic text-lg sm:text-xl leading-relaxed"
          style={{ color: C.gold, fontFamily: serif, fontWeight: 400 }}
          data-testid="river-closing-line"
        >
          From moments to memory. From memory to story. From story to legacy.
        </motion.p>
      </div>
    </section>
  );
}
